import { translate } from '@/translation/translate';
import { Slide } from '../Slide';
import { FC, useEffect, useMemo, useCallback, useRef } from 'react';
import { FeedbackSlide as SlideType } from '@/eventConfig/types.ts';
import { useForm, SubmitHandler } from 'react-hook-form';
import { shuffle } from './feedbackUtils';
import { useNavigation } from '@/navigation/NavigationContex';
import { useSendFeedback } from '@/sendFeedback/useSendFeedback';
import { Feedback as FeedbackType } from '@/sendFeedback/actions/useTransmitFeedback';
import { useAppConfig } from '@/appConfig/useAppConfig';
import { ICONS } from '@/Icons.tsx';
import { useLayer } from '@/layers/useLayer';

interface FeedbackProps {
  className?: string;
  slide: SlideType;
}

interface FeedbackInputs {
  feedback: number;
  otherText: string;
}

// eslint-disable-next-line react-refresh/only-export-components
export enum CancellationReason {
  ChangedMind = 1,
  TooLong = 2,
  Technical = 3,
  TooManySteps = 4,
  Other = 5,
}

export const Feedback: FC<FeedbackProps> = ({ ...props }) => {
  const meta = useAppConfig();
  const { next } = useNavigation();
  const { showLayer } = useLayer();
  const { transmitStatus, transmitFeedback } = useSendFeedback();
  const { slide } = props;

  useEffect(() => {
    if (transmitStatus == 'done') {
      next();
    }
    if (transmitStatus == 'error') {
      showLayer({ ref: '@feedbackFailed' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transmitStatus]);

  const feedbackOptions = [
    { value: CancellationReason.ChangedMind, label: translate(slide?.cancellationReasons?.changedMyMind) },
    { value: CancellationReason.TooLong, label: translate(slide?.cancellationReasons?.tooLong) },
    { value: CancellationReason.Technical, label: translate(slide?.cancellationReasons?.technical) },
    { value: CancellationReason.TooManySteps, label: translate(slide?.cancellationReasons?.tooManySteps) },
  ];

  const feedbackOther = [{ value: CancellationReason.Other, label: translate(slide?.cancellationReasons?.other) }];

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const shuffledOptions = useMemo(() => shuffle(feedbackOptions), []);
  const shuffledOptionsWithOther = [...shuffledOptions, ...feedbackOther];

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FeedbackInputs>();

  const onSubmit: SubmitHandler<FeedbackInputs> = (data) => {
    if (transmitStatus === 'sending' || transmitStatus === 'done') return;

    const feedback: FeedbackType = {
      reason: data.feedback,
      other_reason_details: data.otherText,
      event: meta!.eventCode,
    };
    transmitFeedback(feedback);
  };

  const selected = watch('feedback');
  const isSubmitted = transmitStatus === 'done' || isSubmitting;

  // Roving focus across .option radios and the .submit-button with arrow keys
  const formRef = useRef<HTMLFormElement | null>(null);

  const isTextLikeInput = (el: HTMLElement) => {
    const tag = el.tagName.toLowerCase();
    if (tag === 'textarea') return true;
    if (tag !== 'input') return false;
    const type = (el as HTMLInputElement).type;
    return ['text', 'search', 'url', 'email', 'password', 'number', 'tel'].includes(type);
  };

  const onRoveKeyDown = useCallback((e: React.KeyboardEvent<HTMLFormElement>) => {
    const key = e.key;
    if (key !== 'ArrowDown' && key !== 'ArrowUp' && key !== 'ArrowLeft' && key !== 'ArrowRight') return;

    const target = e.target as HTMLElement;
    // Don't hijack arrow keys while editing text-like inputs unless with Ctrl/Meta
    if (!e.ctrlKey && !e.metaKey && isTextLikeInput(target)) {
      return;
    }

    const container = formRef.current;
    if (!container) return;

    const optionDivs = Array.from(container.querySelectorAll<HTMLElement>('.option'));
    const submitBtn = container.querySelector<HTMLButtonElement>('button.submit-button');

    const stops: HTMLElement[] = [
      ...optionDivs
        .map((div) => div.querySelector<HTMLElement>('input[type="radio"]'))
        .filter((el): el is HTMLElement => !!el),
      ...(submitBtn ? [submitBtn] : []),
    ];
    if (stops.length === 0) return;

    const active = (document.activeElement as HTMLElement) || target;
    const currentIndex = stops.findIndex((el) => el === active || el.contains(active));
    const backwards = key === 'ArrowUp' || key === 'ArrowLeft';

    let nextIndex: number;
    if (currentIndex === -1) {
      nextIndex = backwards ? stops.length - 1 : 0;
    } else if (backwards) {
      nextIndex = currentIndex <= 0 ? stops.length - 1 : currentIndex - 1;
    } else {
      nextIndex = currentIndex >= stops.length - 1 ? 0 : currentIndex + 1;
    }

    e.preventDefault();
    const nextEl = stops[nextIndex];

    if (nextEl instanceof HTMLInputElement && nextEl.type === 'radio') {
      // Emulate native radio arrow behavior: focus and select
      nextEl.focus();
      nextEl.click();
    } else {
      nextEl.focus();
    }
  }, []);

  return (
    <Slide {...props}>
      <div className="feedback-form">
        <div className="question" id="feedback-question">
          <b>{translate(slide?.cancellationReasons?.why)}</b>
        </div>
        <div>
          <form ref={formRef} onKeyDown={onRoveKeyDown} onSubmit={handleSubmit(onSubmit)}>
            <div
              role="radiogroup"
              aria-labelledby="feedback-question"
              aria-required="true"
              aria-describedby={errors.feedback ? 'feedback-error' : undefined}
            >
              {shuffledOptionsWithOther.map((option) => (
                <div className="option" key={option.value}>
                  <label>
                    <input
                      type="radio"
                      value={option.value}
                      aria-invalid={!!errors.feedback || undefined}
                      aria-describedby={errors.feedback ? 'feedback-error' : undefined}
                      {...register('feedback', {
                        required: translate(slide?.cancellationReasons?.pleaseSelectAReason),
                      })}
                    />
                    <span style={{ marginInlineStart: '10px' }}>
                      {option.label}
                      {selected == CancellationReason.Other && option.value == CancellationReason.Other && (
                        <span>
                          :{' '}
                          <input
                            id="feedback-other-text"
                            type="text"
                            placeholder={translate(slide?.cancellationReasons?.pleaseEnterReason)}
                            aria-invalid={!!errors.otherText || undefined}
                            aria-describedby={errors.otherText ? 'otherText-error' : undefined}
                            {...register('otherText', {
                              required:
                                selected == CancellationReason.Other
                                  ? translate(slide?.cancellationReasons?.pleaseProvideAReason)
                                  : false,
                            })}
                          />
                        </span>
                      )}
                    </span>
                  </label>
                </div>
              ))}
            </div>
            <div className="errors" aria-live="assertive">
              {errors.feedback && (
                <p id="feedback-error" role="alert" style={{ color: 'red' }}>
                  {errors.feedback.message}
                </p>
              )}
              {errors.otherText && (
                <p id="otherText-error" role="alert" style={{ color: 'red' }}>
                  {errors.otherText.message}
                </p>
              )}
            </div>
            <div className="form-submit">
              <button
                disabled={isSubmitted}
                aria-disabled={isSubmitted || undefined}
                aria-busy={transmitStatus == 'sending' || undefined}
                className="submit-button"
                type="submit"
              >
                {transmitStatus == 'sending' ? (
                  <i className={ICONS.spinner} aria-hidden="true" />
                ) : (
                  translate(slide?.cancellationReasons?.cancelRegistration)
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Slide>
  );
};
